import {DateTime} from 'luxon';
import RRule, {RRuleSet} from 'rrule';

import {randomString, upperCaseFirst} from '../../../libs/shared/core/src/helpers/utilities/stringHelpers';
import {
  buildContactForHandle,
  ContactSourcesEnum,
} from '../../../libs/shared/core/src/models/api/contactModel';
import {logger} from '../../../libs/shared/core/src/services/logger/logger';
import {parseRecurrenceRuleFromString} from '../../../srcCommon/helpers/models/recurrenceHelpers';
import {CalendarWeekDaysEnum} from '../../models/api/calendarCommonTypes';
import {
  CalendarEventModel,
  CalendarEventStatusesEnum,
  CalendarEventVisualStatusesEnum,
  CompleteCalendarEventModel,
} from '../../models/api/calendarEventModel';
import {
  ParticipantModel,
  ParticipantResponseStatusesEnum,
  ParticipantTypesEnum,
} from '../../models/api/participantModel';
import {
  RecurrenceFrequenciesEnum,
  RecurrenceModel,
  RecurrenceRuleModel,
} from '../../models/api/recurrenceModel';
import {VisualParticipantModel} from '../../models/api/visualParticipantModel';
import {buildPublicId} from '../../models/applications/publicIdTypes';

/*
 * Computed properties.
 */

export function buildCalendarEventPublicId(calendarEvent: CompleteCalendarEventModel) {
  return buildPublicId('calendarEvent', calendarEvent.id);
}

/*
 * Helpers.
 */

export function maybeBuildRecurrenceText(recurrence: ReadonlyArray<string> | undefined): string | undefined {
  if (!recurrence || recurrence.length === 0) {
    return undefined;
  }

  // Wrap in try/catch because rrule can throw error if recurrence text provided in original event is malformed.
  try {
    const rruleSet = parseRecurrenceRuleFromString(recurrence.join('\n'));

    // parseRecurringRuleFromString's return type is RRule | RRuleSet, but it should always be RRuleSet.
    // We handle both cases, just in case.
    const ruleText =
      rruleSet instanceof RRuleSet
        ? rruleSet
            .rrules()
            .map((rrule: RRule) => rrule.toText())
            .join(', ')
        : rruleSet.toText();

    // Return the capitalized rule text.
    return upperCaseFirst(ruleText);
  } catch (e) {
    logger.warn(`Failed to parse recurrence text ${recurrence}`);
    return undefined;
  }
}

const FrontRRuleFrequencyToLibraryFrequency = {
  [RecurrenceFrequenciesEnum.DAILY]: RRule.DAILY,
  [RecurrenceFrequenciesEnum.WEEKLY]: RRule.WEEKLY,
  [RecurrenceFrequenciesEnum.MONTHLY]: RRule.MONTHLY,
  [RecurrenceFrequenciesEnum.YEARLY]: RRule.YEARLY,
  [RecurrenceFrequenciesEnum.HOURLY]: RRule.HOURLY,
  [RecurrenceFrequenciesEnum.MINUTELY]: RRule.MINUTELY,
  [RecurrenceFrequenciesEnum.SECONDLY]: RRule.SECONDLY,
};

const FrontRRuleWeekdayToLibraryWeekday = {
  [CalendarWeekDaysEnum.MONDAY]: RRule.MO,
  [CalendarWeekDaysEnum.TUESDAY]: RRule.TU,
  [CalendarWeekDaysEnum.WEDNESDAY]: RRule.WE,
  [CalendarWeekDaysEnum.THURSDAY]: RRule.TH,
  [CalendarWeekDaysEnum.FRIDAY]: RRule.FR,
  [CalendarWeekDaysEnum.SATURDAY]: RRule.SA,
  [CalendarWeekDaysEnum.SUNDAY]: RRule.SU,
};

export function buildRecurrenceTextForFrontRecurrence(recurrence: RecurrenceModel) {
  if (recurrence.rrules.length === 0) {
    return null;
  }

  // Wrap in try/catch because rrule can throw error if recurrence is malformed
  try {
    const libraryRRule = frontRRuleToLibraryRRule(recurrence.rrules[0]);
    return upperCaseFirst(libraryRRule.toText());
  } catch (e) {
    logger.warn(`Failed to parse recurrence text ${recurrence}`);
    return null;
  }
}

function frontRRuleToLibraryRRule(frontrrule: RecurrenceRuleModel) {
  return new RRule({
    freq: FrontRRuleFrequencyToLibraryFrequency[frontrrule.freq],
    dtstart: frontrrule.dtstart ? DateTime.fromMillis(frontrrule.dtstart).toJSDate() : null,
    interval: frontrrule.interval || 1,
    wkst: frontrrule.wkst ? FrontRRuleWeekdayToLibraryWeekday[frontrrule.wkst] : null,
    count: frontrrule.count || null,
    until: frontrrule.until ? DateTime.fromMillis(frontrrule.until).toJSDate() : null,
    tzid: frontrrule.tzid || null,
    bysetpos: frontrrule.bysetpos ? [...frontrrule.bysetpos] : null,
    bymonth: frontrrule.bymonth ? [...frontrrule.bymonth] : null,
    bymonthday: frontrrule.bymonthday ? [...frontrrule.bymonthday] : null,
    byyearday: frontrrule.byyearday ? [...frontrrule.byyearday] : null,
    byweekno: frontrrule.byweekno ? [...frontrrule.byweekno] : null,
    byweekday: frontrrule.byweekday
      ? frontrrule.byweekday.map((frontWeekday) => {
          const rruleJsWeekday = FrontRRuleWeekdayToLibraryWeekday[frontWeekday.weekday];
          return frontWeekday.n ? rruleJsWeekday.nth(frontWeekday.n) : rruleJsWeekday;
        })
      : null,
    byhour: frontrrule.byhour ? [...frontrrule.byhour] : null,
    byminute: frontrrule.byminute ? [...frontrrule.byminute] : null,
    bysecond: frontrrule.bysecond ? [...frontrrule.bysecond] : null,
  });
}

export function buildCalendarEvent(
  calendarEventVersionUid: string,
  participants?: ReadonlyArray<ParticipantModel>,
): CalendarEventModel {
  return {
    uid: randomString(),
    status: CalendarEventStatusesEnum.CONFIRMED,
    visualStatus: CalendarEventVisualStatusesEnum.DRAFT,
    participants: participants || [],
    calendarEventVersionUids: [calendarEventVersionUid],
    recurrence: undefined,
    recurringCalendarEventId: undefined,
    originalStartDate: undefined,
    attendeesCanInviteOthers: true,
    attendeesCanModify: false,
    shouldIgnoreAttendeeResponse: false,
    updatedAt: 0,
  };
}

export function addOrganizerToVisualParticipants(
  visualParticipants: ReadonlyArray<VisualParticipantModel>,
  organizerAddress?: string,
  shouldAddOrganizerAsAttendee?: boolean,
) {
  if (!organizerAddress) {
    return visualParticipants;
  }

  // If we already have a visualParticipant with the same address, make sure to mark them as the organizer.
  // Otherwise, add the organizer to the list as a non-attendee.
  const existingParticipant = visualParticipants.find((vp) => vp.contact.handle === organizerAddress);
  if (existingParticipant) {
    return visualParticipants.map((vp) => {
      if (vp.contact.handle === organizerAddress) {
        return {
          ...vp,
          isAttendee: shouldAddOrganizerAsAttendee || vp.isAttendee,
          isOrganizer: true,
          responseStatus: ParticipantResponseStatusesEnum.ACCEPTED,
        };
      }

      return vp;
    });
  }

  // By this point, the organizer must not be an attendee.
  const organizerVisualParticipant = {
    namespace: 'global',
    contact: buildContactForHandle(ContactSourcesEnum.EMAIL, organizerAddress),
    type: ParticipantTypesEnum.REQUIRED,
    isOrganizer: true,
    isCreator: false,
    isAttendee: shouldAddOrganizerAsAttendee || false,
    responseStatus: ParticipantResponseStatusesEnum.ACCEPTED,
  };

  return [organizerVisualParticipant, ...visualParticipants];
}

/**
 * Returns startDateTime set to the following hour if today is focused,
 * or to defaultEventStartHour on focusedDate
 */
export function findDefaultEventStartForFocusedDate(focusedDate: number): DateTime<true> {
  const focusedDateTime = DateTime.fromMillis(focusedDate);
  const todayDateTime = DateTime.local();
  const defaultEventStartHour = 12;
  const fallback = todayDateTime.plus({hours: 1}).startOf('hour');

  if (todayDateTime.hasSame(focusedDateTime, 'day')) {
    return fallback;
  }

  if (focusedDateTime.isValid) {
    return focusedDateTime.set({hour: defaultEventStartHour}).startOf('hour');
  }

  return fallback;
}

export const LuxonWeekdayToCalendarWeekDays: {[weekday: string]: CalendarWeekDaysEnum} = {
  1: CalendarWeekDaysEnum.MONDAY,
  2: CalendarWeekDaysEnum.TUESDAY,
  3: CalendarWeekDaysEnum.WEDNESDAY,
  4: CalendarWeekDaysEnum.THURSDAY,
  5: CalendarWeekDaysEnum.FRIDAY,
  6: CalendarWeekDaysEnum.SATURDAY,
  7: CalendarWeekDaysEnum.SUNDAY,
} as const;
